<template>
    <div id="setup-element">
        <!--Stripe.js injects the Payment Element-->
    </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { graphql } from '~/resources/graphql'
import {
    loadStripe,
    type Stripe,
    type StripeElements,
    type StripeError
} from '@stripe/stripe-js'
import { useRpQuery } from '~/composables/graphql'
import { useCheckoutFormProcessing } from '~/components/Checkout/Forms/use-checkout-form'
import type { TokenPaymentMethod } from '~/components/Checkout/Sections/Payment/payment-method'

const props = defineProps<{
    paymentSourceId: string
}>()

const emit = defineEmits<{
    (e: 'paymentMethod', payload: TokenPaymentMethod | undefined): void
    (e: 'displayStripeError', payload: StripeError): void
}>()

const paymentSourceData = ref<{
    stripeSetupIntentClientSecret: string
    stripeAccountId: string | null
    stripeReturnUrl: string
    stripePublishKey: string
    stripeCurrency: string
}>()

let stripe: Stripe | null
let elements: StripeElements

const { incrementProcessing, decrementProcessing } = useCheckoutFormProcessing()

//--------------------------------------------
// initialize Stripe form
//--------------------------------------------

const query = useRpQuery()
onMounted(async () => {
    incrementProcessing()
    const response = await query(
        graphql(/* GraphQL */ `
            query StripeSetupFormQuery($paymentSourceId: ID!) {
                node(id: $paymentSourceId) {
                    __typename
                    ... on PaymentSource {
                        data {
                            __typename
                            ... on PaymentSourceCreditCardStripe {
                                setupIntent {
                                    stripeSetupIntentClientSecret
                                    stripeAccountId
                                    stripeReturnUrl
                                    stripePublishKey
                                    stripeCurrency
                                }
                            }
                        }
                    }
                }
            }
        `),
        {
            paymentSourceId: props.paymentSourceId
        }
    )

    if (
        response.node === null ||
        response.node.__typename !== 'PaymentSource' ||
        response.node.data === null ||
        response.node.data.__typename !== 'PaymentSourceCreditCardStripe'
    ) {
        throw new Error()
    }

    paymentSourceData.value = response.node.data.setupIntent

    stripe = await loadStripe(paymentSourceData.value.stripePublishKey, {
        // Convert null to undefined
        stripeAccount: paymentSourceData.value.stripeAccountId ?? undefined
    })
    if (stripe) {
        elements = stripe.elements({
            clientSecret: paymentSourceData.value.stripeSetupIntentClientSecret,
            currency: paymentSourceData.value.stripeCurrency
        })
    }
    const paymentElement = elements.create('payment', {})
    paymentElement.on('ready', () => {
        decrementProcessing()
    })
    paymentElement.mount('#setup-element')
})

//--------------------------------------------
// submit stripe form - called from parent
// to get stored payment method token
//--------------------------------------------
async function submitStripeElementsForm() {
    if (stripe === null) {
        throw new Error('Stripe object not initialized')
    }

    if (paymentSourceData.value === undefined) {
        throw new Error('Payment source data not initialized')
    }

    const result = await stripe.confirmSetup({
        elements,
        confirmParams: {
            expand: ['payment_method'],
            return_url: paymentSourceData.value.stripeReturnUrl
        },
        redirect: 'if_required'
    })

    if (result.setupIntent) {
        if (result.setupIntent.payment_method === null) {
            throw new Error('Payment method null')
        }
        if (typeof result.setupIntent.payment_method === 'string') {
            throw new Error('Payment method is not expanded')
        }

        //
        // post token and success message to parent
        //
        const paymentMethod: TokenPaymentMethod = {
            type: 'TokenPaymentMethod',
            token: 'STRIPE_PM|' + result.setupIntent.payment_method.id,
            isCard: false,
            paymentTodayAllowed: false,
            brand: result.setupIntent.payment_method.type,
            lastFour: null,
            accountExpirationDate: null
        }

        // We have a card - so add some details
        if (result.setupIntent.payment_method.card !== undefined) {
            paymentMethod.isCard = true
            paymentMethod.brand =
                result.setupIntent.payment_method.card.brand.toLocaleUpperCase()
            paymentMethod.lastFour =
                result.setupIntent.payment_method.card.last4
            paymentMethod.accountExpirationDate =
                result.setupIntent.payment_method.card.exp_year +
                '-' +
                result.setupIntent.payment_method.card.exp_month +
                '-01'
        }

        emit('paymentMethod', paymentMethod)
    } else if (result.error) {
        emit('displayStripeError', result.error)
    }
}

defineExpose({
    submitStripeElementsForm
})
</script>
